import { Component, HostBinding, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  APP_ZONES,
  BaseCssProperties,
  BaseWidgetProperties,
  CountryOption,
  LayoutWidgetConfig,
  ProviderClass,
  UiZoneWidgetConfig,
  WidgetFields,
  WidgetNames,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Subject, finalize, map, takeUntil } from 'rxjs';
import { AutoSuggestAddressResponseItems } from '../../../../lib/dtos/address.dto';
import { Country } from '../../../../lib/dtos/countries.dto';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { MetadataService } from '../../../../lib/services/metadata-service/metadata-service.service';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../lib/services/multipart-holder/multipart-form-holder.service';

import { Router } from '@angular/router';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { setCssAttributes } from '@app/finxone-web-frontend/app/lib/utils/utils';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import { Store } from '@ngxs/store';
import { StyleServiceService } from '../../../../lib/services/style-service/style-service.service';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { FormSubmitter } from '../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AddressComponent extends FormSubmitter implements OnInit {
  @AutoDestroy override destroy$: Subject<void> = new Subject<void>();
  @HostBinding('style.--borderStyle') BorderStyle: string;
  @HostBinding('style.--borderWidth') BorderWidth: string;
  @HostBinding('style.--borderColor') BorderColor: string;
  @HostBinding('style.--borderTopLeftRadius') BorderTopLeftRadius: string;
  @HostBinding('style.--borderBottomLeftRadius') BorderBottomLeftRadius: string;
  @HostBinding('style.--borderTopRightRadius') BorderTopRightRadius: string;
  @HostBinding('style.--borderBottomRightRadius') BorderBottomRightRadius: string;
  @HostBinding('style.--backgroundColor') BackgroundColor: string;
  @HostBinding('style.--inputHeight') InputHeight: string;
  @HostBinding('style.--inputWidth') InputWidth: string;
  @HostBinding('style.--color') Color: string;

  public zone = formatNameAsUrl(APP_ZONES.ONBOARDING);
  public widgetConfig: UiZoneWidgetConfig;
  public userProfileData: any;
  filteredAddress: AutoSuggestAddressResponseItems = [];
  isSearchingAddress = false;
  isCountryDisabled = false;
  countriesList: CountryOption[] = [];
  filteredCountries: CountryOption[] = [];
  selectedCountry: CountryOption = {
    name: '',
    dial_code: '',
    code: '',
  };
  isShowErrorMsg = false;
  isAnyFieldRequired = false;
  addressForm: FormGroup;
  isStepComplete = false;
  isError = false;
  showPopup = false;
  widgetsAttributes: LayoutWidgetConfig[] = [];
  nameString = '';
  urlParams: string[] = [];
  currentWidgetPosition: number;

  colorTextAlignStyle: baseCssConfigStyle;
  colorStyle: baseCssConfigStyle;
  borderStyle: baseCssConfigStyle;
  textInputFontStyle: baseCssConfigStyle;
  textInputBackgroundColor: baseCssConfigStyle;
  isWorkflow: boolean;
  nextPageUrl: string;
  public isAddressShowSuccessIcon = false;
  public isAddressShowErrorIcon = false;
  public combinedTextInputStyles: baseCssConfigStyle;
  combinedContentAndBorderStyles: baseCssConfigStyle;
  public addressInputFieldsStyles: { [key: string]: baseCssConfigStyle } = {};
  private inputStyle: baseCssConfigStyle;
  private inputBaseStyle: baseCssConfigStyle;
  private inputInactiveStyle: baseCssConfigStyle;
  private defaultAddress = {
    county: '',
    address1: '',
    address2: '',
    address3: '',
    address4: '',
    city: '',
    postalCode: '',
    country: '',
  };

  private metadataService = inject(MetadataService);
  private configService = inject(ConfigService);
  private formHolderService = inject(MultiPartFormHolderService);
  public dataIn = inject<ProviderClass<any>>(ProviderClass);
  private urlZoneInput = inject(ZonePageUrlProvider);
  protected _router = inject(Router);
  private styleService = inject(StyleServiceService);
  private formSubmissionService = inject(FormSubmissionService);
  public pageConfig = inject(PageConfigurationProvider);

  // Injecting Store via super() to avoid conflicts with the parent class FormSubmitter which also injects Store
  constructor() {
    super(inject(Store));
    this.zone = this.urlZoneInput.zoneUrlInput;
  }

  ngOnInit(): void {
    this.getCountriesList();
    this.setCustomStyles();
    this.userProfileData = this.store.selectSnapshot(ProfileState.getProfile);
    this.selectedCountry = {
      name: '',
      dial_code: '',
      code: '',
    };
    if (this.zone !== formatNameAsUrl(APP_ZONES.ONBOARDING)) {
      this.selectedCountry = this.countriesList.find((option) => {
        return option.code === this.userProfileData.country;
      }) ?? {
        name: '',
        dial_code: '',
        code: '',
      };
      this.defaultAddress = {
        county: this.userProfileData?.county,
        address1: this.userProfileData?.address1,
        address2: this.userProfileData?.address2,
        address3: this.userProfileData?.address3,
        address4: this.userProfileData?.address4,
        city: this.userProfileData?.city,
        postalCode: this.userProfileData?.postalCode,
        country: this.userProfileData?.country,
      };
    }

    this.createFormStage();

    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    this.setWidgetDataFromDataIn();
    this.buildForm();
    this.setCssAttributes();
    this.stateUpdatesListener();
    this.checkAddressSuccessErrorIconFlag();
  }

  public get searchAddressFieldAttribute() {
    return this.widgetConfig.attributes['field_attributes'].find(
      (field: WidgetFields) => field.type === 'search_address',
    );
  }

  private checkAddressSuccessErrorIconFlag() {
    if (this.dataIn && this.widgetConfig) {
      const showSuccessErrorIcon = this.isShowAndErorIcon(this.widgetConfig.attributes['elementsConfig']);
      this.isAddressShowErrorIcon = showSuccessErrorIcon.isShowErrorIcon;
      this.isAddressShowSuccessIcon = showSuccessErrorIcon.isShowSuccessIcon;
    }
  }

  setWidgetDataFromDataIn() {
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }
  }

  createFormStage() {
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.ADDRESS_WIDGET,
      route: '/onboard-individual/register',
      form: new FormGroup({
        country: new FormControl(this.selectedCountry ?? '', Validators.required),
        county: new FormControl(this.defaultAddress.county ?? ''),
        line_1: new FormControl(this.defaultAddress.address1 ?? '', Validators.required),
        line_2: new FormControl(this.defaultAddress.address2 ?? ''),
        line_3: new FormControl(this.defaultAddress.address3 ?? ''),
        line_4: new FormControl(this.defaultAddress.address4 ?? ''),
        city: new FormControl(this.defaultAddress.city ?? '', Validators.required),
        post_code: new FormControl(this.defaultAddress.postalCode ?? '', Validators.required),
      }),
    });
    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);
  }

  private stateUpdatesListener() {
    this.subscribeToFormAction()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response?.nextPageUrl) this.nextPageUrl = response.nextPageUrl;
        if (response?.type === 'submitStep') this.onSubmit();
        if (response?.formData?.['country']) {
          this.patchFormValues(response.formData);
        }
      });
  }

  setCssAttributes() {
    const { contentStyle, colorStyle, borderStyle, textInputFontStyle, textInputBackgroundColor } =
      setCssAttributes(this.styleService, this.configService, this.attri);

    this.combinedContentAndBorderStyles = {
      ...contentStyle,
      ...borderStyle,
    };
    this.contentStyle = contentStyle;
    this.colorTextAlignStyle = {
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
        BaseCssProperties.FONT_SIZE,
        BaseCssProperties.TEXT_FONT_STYLE,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.COLOR,
      ]),
    };
    this.colorStyle = colorStyle;
    this.borderStyle = borderStyle;
    this.textInputFontStyle = textInputFontStyle;
    this.textInputBackgroundColor = textInputBackgroundColor;

    this.combinedTextInputStyles = {
      ...textInputFontStyle,
      ...textInputBackgroundColor,
    };
    this.getAndAssignInputFieldsStyles(this.attri.widgetProperties);
  }

  private getAndAssignInputFieldsStyles(widgetProperties: BaseWidgetProperties) {
    const { baseStyle, activeStyle, inactiveStyle } =
      this.configService.getInputFieldCssStyles(widgetProperties);

    this.inputStyle = baseStyle;
    this.inputBaseStyle = activeStyle;
    this.inputInactiveStyle = inactiveStyle;

    this.widgetConfig.attributes['field_attributes']?.forEach((field: any) => {
      this.addressInputFieldsStyles[field.name] = {
        ...this.inputInactiveStyle,
        ...this.inputBaseStyle,
      };
    });

    this.setCustomStyles();
  }

  getCountriesList() {
    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((countries: Country[]) => {
          this.countriesList = countries;
          if (this.userProfileData?.country) {
            const country = this.countriesList.find(
              (x: CountryOption) => x.code == this.userProfileData.country,
            );
            if (country) {
              this.selectedCountry = country;
              this.addressForm?.get('country')?.setValue(country.code);
            }
          }
        }),
      )
      .subscribe();
  }

  buildForm() {
    const stage = this.formHolderService.config.getStage(WidgetNames.ADDRESS_WIDGET);
    this.addressForm = stage.getForm();
    this.addressForm.enable(); //make sure the fields are enabled
    this.onAutoCompleteAddressChange(this.selectedCountry.code || null);
  }

  patchFormValues(formData: Record<string, string>) {
    this.addressForm.get('country')?.setValue(formData?.['country']);
    this.addressForm.get('county')?.setValue(formData?.['county']);
    this.addressForm.get('line_1')?.setValue(formData?.['line_1']);
    this.addressForm.get('line_2')?.setValue(formData?.['line_2']);
    this.addressForm.get('line_3')?.setValue(formData?.['line_3']);
    this.addressForm.get('line_4')?.setValue(formData?.['line_4']);
    this.addressForm.get('city')?.setValue(formData?.['city']);
    this.addressForm.get('post_code')?.setValue(formData?.['post_code']);
    const formValues: Record<string, string> = this.addressForm.value;
    let name = '';
    Object.values(formValues).forEach((value: string) => {
      name = name + value + ' ';
    });
    const country = this.countriesList.find((x: CountryOption) => x.code == formData?.['country']);
    if (country) {
      this.selectedCountry = country;
      this.addressForm.get('country')?.setValue(country.code);
    }
    this.nameString = name;
    this.isStepComplete = true;
    this.isError = false;
  }

  onSubmit() {
    if (this.addressForm.valid) {
      const formValues: Record<string, string> = this.addressForm.value;
      this.formSubmissionService.submitIndividualForm(formValues, this.nextPageUrl).then((success) => {
        if (success) {
          let name = '';
          Object.values(formValues).forEach((value: string) => {
            name = name + value + ' ';
          });
          this.nameString = name;
          this.isStepComplete = true;
          this.isError = false;
        } else {
          return;
        }
      });
    } else {
      this.addressForm.markAllAsTouched();
      this.isStepComplete = false;
      this.isError = true;
      this.showPopup = true;
    }
  }

  public searchAddresses(query: string) {
    if (query.length >= 4) {
      this.isSearchingAddress = true;
      this.metadataService
        .autosuggestAddress(query)
        .pipe(
          takeUntil(this.destroy$),
          map((v) => {
            this.filteredAddress = v;
          }),
          finalize(() => (this.isSearchingAddress = false)),
        )
        .subscribe();
    } else {
      this.isSearchingAddress = false;
      this.filteredAddress = [];
    }
  }

  onAddressOptionSelected(addressId: string | null): void {
    if (addressId) {
      this.getAddressByLocation(addressId);
    }
  }

  getAddressByLocation(addressId: string) {
    this.metadataService
      .getAddressById(addressId)
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          this.selectedCountry = {
            name: response.country,
            code: 'GB',
            dial_code: '+44',
          };
          response.country = 'GB';
          this.addressForm.patchValue({ ...response, post_code: response.postcode });
          this.addressForm.enable(); //make sure the fields are enabled
        }),
      )
      .subscribe();
  }

  filterCountries(query: string) {
    const filtered: any[] = [];
    for (const element of this.countriesList) {
      const type = element;
      if (type.name.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        filtered.push(type);
      }
    }
    this.filteredCountries = filtered;
  }

  public onAutoCompleteAddressChange(code: string | null) {
    const country = code ? this.countriesList.find((option) => option.code === code) : null;
    this.selectedCountry = country ?? { name: '', code: '', dial_code: '' };
    this.addressForm.get('country')?.setValue(this.selectedCountry.code);
  }
  showErrorMsg() {
    this.isShowErrorMsg = !this.isShowErrorMsg;
  }

  public updateInputFieldCurrentStyles(event: boolean, fieldName: string) {
    if (event) {
      this.addressInputFieldsStyles[fieldName] = {
        ...this.inputStyle,
        ...this.inputBaseStyle,
      };
    } else {
      this.addressInputFieldsStyles[fieldName] = {
        ...this.inputInactiveStyle,
        ...this.inputBaseStyle,
      };
    }
  }

  setCustomStyles() {
    this.BorderWidth = this.inputStyle?.['border-width'] || '1px';
    this.BorderColor = this.inputInactiveStyle?.['border-color'] || 'var(--color-border-color)';
    this.BorderStyle = this.inputStyle?.['border-style'] || 'solid';
    this.BorderTopLeftRadius = this.inputStyle?.['border-top-left-radius'] || '5px';
    this.BorderBottomLeftRadius = this.inputStyle?.['border-bottom-left-radius'] || '5px';
    this.BorderTopRightRadius = this.inputStyle?.['border-top-right-radius'] || '5px';
    this.BorderBottomRightRadius = this.inputStyle?.['border-bottom-right-radius'] || '5px';
    this.BackgroundColor = this.inputInactiveStyle?.['background-color'] || 'var(--color-primary)';
    this.InputHeight = this.inputBaseStyle?.['height'] || '44px';
    this.InputWidth = this.inputBaseStyle?.['width'] || '100%';
    this.Color = this.inputBaseStyle?.['color'] || 'var(--color-background';
  }

  get isAddressFieldEnabled() {
    return (
      this.zone === formatNameAsUrl(APP_ZONES.ONBOARDING) ||
      this.zone === formatNameAsUrl(APP_ZONES.ORGANISATION_ONBOARDING)
    );
  }
}
